import React, { useState } from 'react';
import { ScrollView, TouchableOpacity, TextInput, Image, Platform } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { X, Star, User, PartyPopper } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '@/components';
import { LinearGradient } from '@/components/GradientView';
import { COLORS } from '@/constants/colors';
import { useNavigation } from '@react-navigation/native';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';

const MeetingCompletedScreen: React.FC = () => {
  const isMobileLayout = useIsMobileLayout();
  const { t } = useTranslation();
  const themeName = useThemeName();
  const navigation = useNavigation();
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  const userPhotoUri: string | undefined = undefined;
  
  const titleColor = themeName === 'dark' ? '#ffffff' : '#18181b';
  const cardBackgroundColor = themeName === 'dark' ? '#1f1f1f' : '#ffffff';
  const borderColor = themeName === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
  const gradientColors = themeName === 'dark'
    ? [COLORS.DARK_GRADIENT_START, COLORS.DARK_GRADIENT_END]
    : [COLORS.LIGHT_GRADIENT_START, COLORS.LIGHT_GRADIENT_END];

  const handleGoToMain = () => {
    navigation.navigate('Home' as never);
  };

  const handleSubmitRating = () => {
    handleGoToMain();
  };

  // For web with desktop layout, center content and limit width
  const containerStyle = Platform.OS === 'web' && !isMobileLayout 
    ? { maxWidth: 600, alignSelf: 'center', width: '100%' }
    : {};

  return (
    <YStack flex={1} backgroundColor={themeName === 'dark' ? '#000000' : '#f5f5f5'} padding="$4" style={containerStyle}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 32, flexGrow: 1 }}
      >
        <YStack
          backgroundColor={cardBackgroundColor}
          borderRadius="$6"
          marginTop="$8"
          elevation={8}
          shadowColor="rgba(0,0,0,0.15)"
          borderWidth={1}
          borderColor={borderColor}
          marginBottom="$4"
          overflow="hidden"
        >
          <LinearGradient
            colors={gradientColors}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={{ borderBottomLeftRadius: 24, borderBottomRightRadius: 24 }}
          >
          <YStack
            padding="$4"
            alignItems="center"
            space="$3"
            borderTopLeftRadius="$6"
            borderTopRightRadius="$6"
            overflow="hidden"
          >
            <XStack width="100%" justifyContent="flex-start">
              <TouchableOpacity
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: 'rgba(255,255,255,0)',
                  borderWidth: 1,
                  borderColor: '#ffffff',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <X size={16} color="#ffffff" />
              </TouchableOpacity>
            </XStack>

            <YStack
              width={130}
              height={130}
              backgroundColor="transparent"
              alignItems="center"
              justifyContent="center"
              marginTop={-14}
            >
              <PartyPopper size={68} color="#ffffff" strokeWidth={1.5} />
            </YStack>

            <Text fontSize={24} fontWeight="bold" fontFamily="Poppins" color="#ffffff" textAlign="center" marginTop={-6}>
              {t('screens.meetingCompleted.title')}
            </Text>

            <Text fontSize={14} color="#ffffff" textAlign="center" lineHeight={20}>
              {t('screens.meetingCompleted.subtitle')}
            </Text>
          </YStack>
          </LinearGradient>

          <YStack padding="$4" space="$3">
            <YStack
              backgroundColor={themeName === 'dark' ? '#181818' : '#ffffff'}
              borderRadius="$6"
              padding="$4"
              borderWidth={1}
              borderColor={borderColor}
              space="$3"
            >
              <Text fontSize={18} fontWeight="600" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                {t('screens.meetingCompleted.meetingDetails')}
              </Text>

              <XStack alignItems="center" space="$3">
                <YStack
                  width={40}
                  height={40}
                  borderRadius={9999}
                  backgroundColor="$gray4"
                  borderWidth={2}
                  borderColor={themeName === 'dark' ? '#ffffff' : '#484848'}
                  alignItems="center"
                  justifyContent="center"
                >
                  {userPhotoUri ? (
                    <Image
                      source={{ uri: userPhotoUri }}
                      style={{ width: 36, height: 36, borderRadius: 9999 }}
                    />
                  ) : (
                    <User size={20} color={themeName === 'dark' ? '#ffffff' : '#484848'} />
                  )}
                </YStack>
                <Text fontSize={16} fontWeight="500" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                  Agata, 23
                </Text>
              </XStack>

              <YStack space="$3">
                <XStack alignItems="center" space="$2">
                  <XStack space="$2" flexShrink={1}>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>{t('screens.meetingCompleted.date')}</Text>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'} flexShrink={1}>27 lipca, 18:00</Text>
                  </XStack>
                </XStack>
                
                <XStack alignItems="center" space="$2">
                  <XStack space="$2" flexShrink={1}>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>{t('screens.meetingCompleted.location')}</Text>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'} flexShrink={1}>Restauracja "Papavero"</Text>
                  </XStack>
                </XStack>
                
                <XStack alignItems="center" space="$2">
                  <XStack space="$2">
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>{t('screens.meetingCompleted.amount')}</Text>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>200,00 zł</Text>
                  </XStack>
                </XStack>
                
                <XStack alignItems="center" space="$2">
                  <XStack space="$2" flexShrink={1}>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>{t('screens.meetingCompleted.type')}</Text>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'} flexShrink={1}>Wspólna kolacja</Text>
                  </XStack>
                </XStack>
              </YStack>
            </YStack>
          </YStack>

          <YStack padding="$4">
            <YStack
              backgroundColor={themeName === 'dark' ? '#181818' : '#ffffff'}
              borderRadius="$6"
              padding="$4"
              borderWidth={1}
              borderColor={borderColor}
              space="$4"
              alignItems="center"
            >
              <Text fontSize={18} fontWeight="600" fontFamily="Poppins" color={titleColor} textAlign="center">
                {t('screens.meetingCompleted.rateUser', { name: 'Agatę' })}
              </Text>

              <XStack space="$2" justifyContent="center" alignItems="center">
                {[1, 2, 3, 4, 5].map((star) => {
                  const isActive = star <= rating;
                  const activeColor = '#fbbf24';
                  const inactiveColor = themeName === 'dark' ? '#4a4a4a' : '#d1d5db';
                  return (
                    <TouchableOpacity
                      key={star}
                      onPress={() => setRating(star)}
                      onLongPress={() => setRating(0)}
                      accessibilityRole="button"
                      accessibilityLabel={t('screens.meetingCompleted.ratingLabel', { count: star })}
                    >
                      <Star
                        size={24}
                        color={isActive ? activeColor : inactiveColor}
                        fill={isActive ? activeColor : 'transparent'}
                      />
                    </TouchableOpacity>
                  );
                })}
              </XStack>

              <YStack
                backgroundColor={themeName === 'dark' ? '#2a2a2a' : '#f9f9f9'}
                borderRadius="$3"
                padding="$3"
                borderWidth={1}
                borderColor={borderColor}
                alignSelf="stretch"
                width="100%"
              >
                <TextInput
                  value={comment}
                  onChangeText={setComment}
                  placeholder={t('screens.meetingCompleted.addComment')}
                  placeholderTextColor={themeName === 'dark' ? '#9ca3af' : '#9ca3af'}
                  multiline
                  numberOfLines={2}
                  style={{
                    color: themeName === 'dark' ? '#ffffff' : '#111827',
                    minHeight: 56,
                    width: '100%',
                    textAlignVertical: 'top',
                  }}
                />
              </YStack>

              <CustomButton
                backgroundColor="#5E809C"
                color="white"
                fontSize={18}
                fontWeight="600"
                paddingVertical="$2"
                paddingHorizontal="$4"
                borderRadius="$4"
                onPress={handleSubmitRating}
              >
                {t('screens.meetingCompleted.submitRating')}
              </CustomButton>
            </YStack>
          </YStack>

          <YStack padding="$4" space="$3" borderTopWidth={1} borderTopColor={borderColor}>
            <XStack space="$3" justifyContent="space-between" alignItems="stretch">
              <YStack borderWidth={1} borderColor="#ef4444" borderRadius="$3" flex={1} alignItems="stretch" justifyContent="center">
                <CustomButton
                  backgroundColor="transparent"
                  color="#ef4444"
                  fontSize={14}
                  fontWeight="500"
                  paddingVertical="$3"
                  paddingHorizontal="$4"
                  borderRadius="$3"
                  textAlign="center"
                >
                  {t('screens.meetingCompleted.reportProblem')}
                </CustomButton>
              </YStack>
              
              <YStack borderWidth={1} borderColor="#5E809C" borderRadius="$3" flex={1} alignItems="stretch" justifyContent="center">
                <CustomButton
                  backgroundColor="transparent"
                  color="#5E809C"
                  fontSize={14}
                  fontWeight="500"
                  paddingVertical="$3"
                  paddingHorizontal="$4"
                  borderRadius="$3"
                  textAlign="center"
                >
                  {t('screens.meetingCompleted.addToFavorites')}
                </CustomButton>
              </YStack>
            </XStack>

            <CustomButton
              backgroundColor="#5E809C"
              color="white"
              fontSize={16}
              fontWeight="600"
              paddingVertical="$3"
              borderRadius="$3"
              onPress={handleGoToMain}
            >
              {t('screens.meetingCompleted.backToMain')}
            </CustomButton>
          </YStack>
        </YStack>
      </ScrollView>
    </YStack>
  );
};

export default MeetingCompletedScreen;
